import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, ArrowRight, User, Lock } from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE_URL } from '../utils/api';
const API_URL = API_BASE_URL;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // Set page title
  useEffect(() => {
    document.title = "Connect | Admin Portal";
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/admin-portal/login`, {
        username,
        password
      });
      
      localStorage.setItem('admin_token', response.data.access_token);
      localStorage.setItem('admin', JSON.stringify(response.data.admin));
      toast.success('Welcome to Admin Portal!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-6">
            <img src="/connect-logo-dark.png" alt="CONNECT" className="w-14 h-14 rounded-xl shadow-lg" />
            <div className="text-left">
              <h1 className="font-bold text-2xl text-white">CONNECT</h1>
              <p className="text-sm text-slate-400">Admin Portal</p>
            </div>
          </div>
          <p className="text-slate-400">
            Platform administration and management
          </p>
        </div>

        <Card className="border-slate-700 bg-slate-800/50 shadow-2xl">
          <CardHeader>
            <CardTitle className="text-white">Administrator Sign In</CardTitle>
            <CardDescription className="text-slate-400">
              Access platform settings, pricing, and partner management
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-slate-300">Username</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="username"
                    placeholder="admin"
                    className="pl-10 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    data-testid="admin-username"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Your password"
                    className="pl-10 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="admin-password"
                  />
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-red-500 to-orange-600 hover:from-red-400 hover:to-orange-500 text-white" 
                disabled={loading}
                data-testid="admin-login-btn"
              >
                {loading ? 'Signing in...' : 'Sign In'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-slate-500">
          <Link to="/" className="hover:text-slate-300 transition-colors">
            ← Back to CONNECT
          </Link>
        </div>
      </div>
    </div>
  );
}
